import { type ReactNode } from "react";

import { NamedIcon } from "~/components/ui/named-icon";
import { safeBuilderText } from "~/lib/builder-utils";
import { cn } from "~/lib/cn";
import type { TIconName } from "~/lib/icon-names";

type IconFeatureGridItem = {
  readonly title: unknown;
  readonly description?: unknown;
  readonly iconName?: TIconName | "";
  readonly badgeNumber?: number | null;
};

type IconFeatureGridProps = {
  readonly preHeadline?: unknown;
  readonly headline?: unknown;
  readonly description?: unknown;
  readonly items?: IconFeatureGridItem[];
  readonly columns?: number | null;
  readonly tone?: "plain" | "muted" | "dark";
};

const iconBadgeClasses = (dark: boolean): string =>
  cn(
    "flex h-12 w-12 items-center justify-center rounded-full text-xl font-semibold",
    dark ? "bg-white/10 text-purple-200" : "bg-brand-50 text-brand-700",
  );

const gridColumns: Record<2 | 3 | 4, string> = {
  2: "md:grid-cols-2",
  3: "md:grid-cols-3",
  4: "md:grid-cols-4",
};

const renderIconBadge = (
  item: IconFeatureGridItem,
  dark: boolean,
): ReactNode => {
  const classes = iconBadgeClasses(dark);

  if (item.badgeNumber !== undefined && item.badgeNumber !== null) {
    return <span className={classes}>{item.badgeNumber}</span>;
  }

  if (item.iconName) {
    return (
      <span className={cn(classes, "text-2xl")}>
        <NamedIcon name={item.iconName} className="h-6 w-6" />
      </span>
    );
  }

  return (
    <span className={classes} aria-hidden="true">
      •
    </span>
  );
};

/** Marketing grid of features, each with an icon, title and text. */
export const IconFeatureGrid = ({
  preHeadline,
  headline,
  description,
  items = [],
  columns = 3,
  tone = "muted",
}: IconFeatureGridProps) => {
  const safePreHeadline = safeBuilderText(preHeadline);
  const safeHeadline = safeBuilderText(headline);
  const safeDescription = safeBuilderText(description);

  if (!safeHeadline && !safeDescription && items.length === 0) {
    return null;
  }

  const normalizedColumns: 2 | 3 | 4 =
    columns === 2 || columns === 4 ? columns : 3;

  const dark = tone === "dark";

  return (
    <section
      className={cn(
        "py-16",
        tone === "muted" && "bg-gradient-to-b from-white to-brand-50/40",
        dark && "bg-gray-950",
      )}
    >
      <div className="mx-auto max-w-screen-xl space-y-8 px-4">
        {(safePreHeadline || safeHeadline || safeDescription) && (
          <div className="flex flex-col gap-4 text-center md:text-left">
            <div>
              {safePreHeadline && (
                <p
                  className={cn(
                    "mb-1 text-sm font-medium tracking-wide uppercase",
                    dark ? "text-purple-300" : "text-purple-700",
                  )}
                >
                  {safePreHeadline}
                </p>
              )}
              {safeHeadline && (
                <h2
                  className={cn(
                    "font-heading text-3xl font-medium tracking-tight md:text-4xl",
                    dark ? "text-white" : "text-text-primary",
                  )}
                >
                  {safeHeadline}
                </h2>
              )}
            </div>
            {safeDescription && (
              <p
                className={cn(
                  "text-lg leading-relaxed",
                  dark ? "text-gray-300" : "text-text-secondary",
                )}
              >
                {safeDescription}
              </p>
            )}
          </div>
        )}

        <div
          className={cn(
            "grid gap-6 sm:grid-cols-2",
            gridColumns[normalizedColumns],
          )}
        >
          {items.map((item, index) => {
            const safeTitle = safeBuilderText(item.title);
            const safeItemDescription = safeBuilderText(item.description);

            return (
              <div
                key={`item-${index}-${safeTitle}-${item.iconName ?? ""}-${item.badgeNumber ?? ""}`}
                className={cn(
                  "group flex h-full flex-col gap-4 rounded-2xl border p-6 transition",
                  dark
                    ? "border-white/10 bg-white/5 text-white backdrop-blur"
                    : "border-brand-100/70 bg-white shadow-sm duration-500 ease-out-expo hover:-translate-y-1 hover:border-brand-200 hover:shadow-xl",
                )}
              >
                <div className="transition-transform duration-200 group-hover:scale-105">
                  {renderIconBadge(item, dark)}
                </div>
                {safeTitle && (
                  <h3
                    className={cn(
                      "font-heading text-xl font-medium",
                      dark ? "text-white" : "text-text-primary",
                    )}
                  >
                    {safeTitle}
                  </h3>
                )}
                {safeItemDescription && (
                  <p
                    className={cn(
                      "leading-relaxed",
                      dark ? "text-gray-200" : "text-text-secondary",
                    )}
                  >
                    {safeItemDescription}
                  </p>
                )}
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
};
